import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Card, CardMedia, CardContent, Button, Chip, CircularProgress, Grid } from '@mui/material';
import { FaTachometerAlt, FaCalendarAlt, FaEuroSign, FaInfoCircle, FaEye, FaCheckCircle, FaExternalLinkAlt, FaGasPump, FaCog, FaComments } from 'react-icons/fa';
import { motion } from 'framer-motion';
import VehicleDialog from './ChatBody/CustomCarousel/VehicleDialog';
import CatalogTrimDialog from './ChatBody/CustomCarousel/CatalogTrimDialog';
import { fetchVehicleData } from '../api/fetchVehicleData';
import { trackChatEvent } from '../utils/analytics';
import { useWebSocketContext } from '../context/Theme';
import { createTranslator, getBrowserLanguage } from '../utils/i18n';

const MotionButton = motion(Button);

const formatPrice = (price, locale = "it-IT") => {
  return parseFloat(price).toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

// Helper function to determine badge color - using consistent brand colors
const getBadgeColor = (badgeText) => {
  if (!badgeText) return '#64748b'; // Default gray
  
  const badgeLower = badgeText.toLowerCase();
  if (badgeLower.includes('nuovo') || badgeLower.includes('nuova')) return '#10b981'; // Green
  if (badgeLower.includes('km 0') || badgeLower.includes('km0')) return '#2196f3'; // Blue
  if (badgeLower.includes('usato')) return '#f59e0b'; // Amber
  if (badgeLower.includes('pronta consegna')) return '#8b5cf6'; // Purple
  if (badgeLower.includes('aziendale')) return '#06b6d4'; // Cyan
  return '#64748b'; // Default gray for other cases
};

/**
 * Professional car carousel for the SearchPanel (Desktop)
 * Enhanced design with improved spacing, typography, and visual hierarchy
 * Now includes fuel information display and enhanced UX/UI
 * 
 * Data Structure Notes:
 * - items[].description1: kilometers (km)
 * - items[].description2: sale price
 * - items[].description3: year
 * - items[].description4: retail price (for discount calculation)
 * - items[].fuel: fuel type (carburante) - ADD THIS FIELD to backend data
 * - items[].description5: alternative field for fuel if needed
 * 
 * To fully implement fuel display, ensure your backend includes:
 * - fuel: string (e.g., "Benzina", "Diesel", "Ibrida", "Elettrica")
 * OR
 * - description5: string containing fuel type
 */
const SearchPanelCarousel = ({ items = [] }) => {
  const { handleSend, isLoading, setIsLoading } = useWebSocketContext();
  const language = useMemo(() => getBrowserLanguage(), []);
  const t = useMemo(() => createTranslator(language), [language]);
  const numberLocale = language === "en" ? "en-US" : language === "es" ? "es-ES" : "it-IT";
  const [open, setOpen] = useState(false);
  const [vehicleData, setVehicleData] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [showFullScreenGallery, setShowFullScreenGallery] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [loadingImages, setLoadingImages] = useState(true);

  // Catalog dialog state
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [selectedCatalogCar, setSelectedCatalogCar] = useState(null);

  useEffect(() => {
    setLoadingImages(true);
    setImagesLoaded(false);

    const imagePromises = items.map((car) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = car.image;
        img.onload = () => resolve();
        img.onerror = () => resolve(); // Also resolve on error to prevent hanging
      });
    });

    Promise.all(imagePromises).then(() => {
      setImagesLoaded(true);
      setLoadingImages(false);
    });
  }, [items]); // Reset loading state when items change

  const isCatalogItem = (car) => car.vehicleid?.startsWith('catalog_');
  const getTrimCode = (car) => car.vehicleid?.replace(/^catalog_/, '') ?? '';

  const handleBookVisit = (car) => {
    const message = t('bookVisitMessage', { title: car.title, vehicleId: car.vehicleid });
    trackChatEvent.bookVisit(car.vehicleid, car.title);
    handleSend(message, null, null, { type: 'chat_message', sender: 'user' });
  };

  const handleRichiediPreventivoStock = (car) => {
    const message = t('requestQuoteStockMessage', { title: car.title, vehicleId: car.vehicleid });
    trackChatEvent.sendMessage(message.length);
    handleSend(message, null, null, { type: 'chat_message', sender: 'user' });
  };

  const handleOpenCatalogDialog = (car) => {
    setSelectedCatalogCar(car);
    setCatalogOpen(true);
  };

  const handleRichiediPreventivoConfig = (car) => {
    const trimCode = getTrimCode(car);
    const message = t('requestQuoteConfigMessage', { title: car.title, trimCode });
    trackChatEvent.sendMessage(message.length);
    handleSend(message, null, null, { type: 'chat_message', sender: 'user' });
  };

  const handleInfoRequest = (car) => {
    const message = t('infoRequestMessage', { title: car.title, vehicleId: car.vehicleid });
    trackChatEvent.sendMessage(message.length);
    
    handleSend(message, null, null, {
      type: 'chat_message',
      sender: 'user'
    });
  };

  const handleImageClick = async (vehicle) => {
    trackChatEvent.viewVehicleDetails(vehicle.vehicleid, vehicle.title);
    setOpen(true);
    setVehicleData(null);
    setSelectedVehicleId(vehicle.vehicleid);
    setShowFullScreenGallery(false);
    
    try {
      const data = await fetchVehicleData(vehicle.vehicleid);
      setVehicleData(data);
    } catch (error) {
      console.error('Error fetching vehicle data:', error);
    }
  };

  const handleDetailsClick = (car, e) => {
    // Stop the event from propagating to prevent navigation triggers
    if (e) {
      e.stopPropagation();
      // Don't prevent default as it might interfere with button behavior
    }
    
    // Use setTimeout to break the event chain
    setTimeout(() => {
      trackChatEvent.viewVehicleDetails(car.vehicleid, car.title);
      setOpen(true);
      setIsLoading(true);
      setVehicleData(null);
      setSelectedVehicleId(car.vehicleid);
      setShowFullScreenGallery(false);
      
      fetchVehicleData(car.vehicleid)
        .then(data => {
          setVehicleData(data);
        })
        .catch(error => {
          console.error('Error fetching vehicle data:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 0);
  };

  const handleClose = () => {
    setOpen(false);
    setShowFullScreenGallery(false);
  };

  if (loadingImages || !imagesLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          width: '100%',
        }}
      >
        <CircularProgress sx={{ color: '#2196f3' }} />
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        {items.map((car, idx) => (
          <Grid item xs={12} md={6} key={idx}>
            <Card
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ y: -6 }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                borderRadius: '18px',
                overflow: 'hidden',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12)',
                  borderColor: 'rgba(33, 150, 243, 0.3)',
                  transform: 'translateY(-4px)',
                },
                bgcolor: '#ffffff',
                position: 'relative',
              }}
            >
              {car.badge && (
                <Box
                  className="vehicle-badge"
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    backgroundColor: getBadgeColor(car.badge),
                    color: '#fff',
                    padding: '6px 12px',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    borderBottomRightRadius: '12px',
                    zIndex: 2,
                    letterSpacing: '0.5px',
                  }}
                >
                  {car.badge}
                </Box>
              )}
              
              {!car.available && (
                <Box
                  className="unavailable-badge"
                  sx={{
                    position: "absolute",
                    top: car.badge ? '32px' : 0,
                    left: 0,
                    backgroundColor: '#ef4444',
                    color: "white",
                    padding: '6px 12px',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    borderBottomRightRadius: '12px',
                    zIndex: 2,
                    letterSpacing: '0.5px',
                  }}
                >
                  {t('unavailable')}
                </Box>
              )}

              <Box
                component={motion.div}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                sx={{
                  width: '100%',
                  height: '160px',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  position: 'relative',
                }}
                onClick={() => handleImageClick(car)}
              >
                {/* Discount Badge - only for new cars */}
                {(() => {
                  const oldPrice = parseFloat(car.description4 || "0");
                  const newPrice = parseFloat(car.description2);
                  const hasDiscount = oldPrice > newPrice && oldPrice > 0;
                  
                  // Check if it's a used car or km0
                  const badgeLower = (car.badge || "").toLowerCase();
                  // Treat km0 cars the same as used cars - don't show discount
                  const isUsedOrKm0Car = 
                    badgeLower !== "nuovo";
                  
                  // Only show discount badge for new cars
                  if (hasDiscount && !isUsedOrKm0Car) {
                    const discountPercentage = Math.round((1 - newPrice / oldPrice) * 100);
                    return (
                      <Box
                        className="discount-badge"
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          backgroundColor: '#ef4444',
                          color: '#fff',
                          padding: '6px 12px',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          borderBottomLeftRadius: '12px',
                          zIndex: 2,
                          letterSpacing: '0.5px',
                        }}
                      >
                        -{discountPercentage}%
                      </Box>
                    );
                  }
                  return null;
                })()}
                
                <CardMedia
                  component="img"
                  src={car.image}
                  alt={car.title ?? 'Car image'}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                  onError={(e) => {
                    const fallbackLogo = localStorage.getItem("logo") || `${process.env.REACT_APP_DOMAIN}/media/company_logos/3_ms-icon-144x144.png`;
                    e.target.src = fallbackLogo;
                    e.target.style.objectFit = 'contain';
                    e.target.style.padding = '20px';
                    e.target.style.backgroundColor = '#f8fafc';
                  }}
                />

                {/* Price ribbon over image */}
                {car.description2 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      px: 1.25,
                      py: 0.75,
                      borderRadius: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 0.25,
                      backdropFilter: 'blur(2px)'
                    }}
                    onClick={(e)=>e.stopPropagation()}
                  >
                    {(() => {
                      const oldP = parseFloat(car.description4 || '0');
                      const newP = parseFloat(car.description2);
                      const badgeLower = (car.badge || '').toLowerCase();
                      const isNew = badgeLower === 'nuovo';
                      const hasDisc = isNew && oldP > newP && oldP > 0;
                      return hasDisc ? (
                        <Typography sx={{ textDecoration: 'line-through', opacity: 0.85, fontSize: '0.75rem', fontWeight: 500 }}>
                          {formatPrice(oldP, numberLocale)} €
                        </Typography>
                      ) : null;
                    })()}
                    <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
                      {formatPrice(car.description2, numberLocale)} €
                    </Typography>
                  </Box>
                )}

                {/* Mobile: title-only overlay on image — CTA buttons live below in CardContent */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    px: 1.25,
                    pt: 2,
                    pb: 1,
                    background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.62) 100%)',
                    display: { xs: 'flex', md: 'none' },
                    alignItems: 'flex-end',
                    pointerEvents: 'none',
                  }}
                >
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#fff', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {car.title}
                  </Typography>
                </Box>
              </Box>

              {/* ── CardContent: visible on all sizes, adapted for mobile ── */}
              <CardContent sx={{ 
                flex: 1, 
                display: 'flex',
                flexDirection: 'column',
                p: { xs: 1.5, md: 3 },
                justifyContent: 'space-between',
                gap: { xs: 1, md: 2 },
              }}>
                <Box>
                  {/* Title hidden on mobile (shown in image overlay) */}
                  <Typography 
                    variant="h6" 
                    className="vehicle-title"
                    sx={{ 
                      fontWeight: 700,
                      fontSize: "1rem",
                      lineHeight: 1.3,
                      marginBottom: "8px",
                      color: "#1e293b",
                      display: { xs: 'none', md: '-webkit-box' },
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      wordBreak: 'break-word',
                    }}
                  >
                    {car.title}
                  </Typography>

                  {/* Verbose content hidden on mobile */}
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    {/* Promotion Details Section - Only show when both promotion and promotionDetails are available */}
                    {car.promotion && car.promotionDetails && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          backgroundColor: "#f5f3ff",
                          borderRadius: "14px",
                          padding: "14px 18px",
                          mb: 1.5,
                          border: "1px solid #ddd6fe",
                          maxHeight: '100px',
                          overflow: 'auto',
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            color: "#7c3aed",
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "20px",
                              height: "20px",
                              borderRadius: "50%",
                              backgroundColor: "#8b5cf6",
                              color: "#fff",
                              fontSize: "12px",
                              fontWeight: "bold",
                            }}
                          >
                            %
                          </Box>
                          {car.promotion}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#4b5563",
                            fontSize: "0.85rem",
                            lineHeight: 1.4,
                          }}
                        >
                          {car.promotionDetails}
                        </Typography>
                        {car.promotion_document__url && (
                          <Button
                            size="small"
                            variant="text"
                            href={car.promotion_document__url}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '16px',
                                  height: '16px',
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                  <polyline points="14 2 14 8 20 8"></polyline>
                                  <line x1="16" y1="13" x2="8" y2="13"></line>
                                  <line x1="16" y1="17" x2="8" y2="17"></line>
                                  <polyline points="10 9 9 9 8 9"></polyline>
                                </svg>
                              </Box>
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              trackChatEvent.viewPromotionDocument && trackChatEvent.viewPromotionDocument(car.vehicleid, car.promotion);
                            }}
                            sx={{
                              color: '#6d28d9',
                              textTransform: 'none',
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              mt: 0.5,
                              padding: '2px 10px',
                              alignSelf: 'flex-start',
                              '&:hover': {
                                backgroundColor: 'rgba(124, 58, 237, 0.08)',
                              },
                            }}
                          >
                            {t('viewDocument')}
                          </Button>
                        )}
                      </Box>
                    )}
                    
                    <Box
                      sx={{
                        display: 'none',
                        alignItems: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                        backgroundColor: (() => {
                          const oldPrice = parseFloat(car.description4 || "0");
                          const newPrice = parseFloat(car.description2);
                          const hasDiscount = oldPrice > newPrice && oldPrice > 0;
                          
                          // Check if it's a used car
                          const badgeLower = (car.badge || "").toLowerCase();
                          // Treat km0 cars the same as used cars - don't show discount
                          const isUsedOrKm0Car = 
                            badgeLower !== "nuovo";
                          
                          // Only use red background for new cars with discount
                          if (hasDiscount && !isUsedOrKm0Car) {
                            return '#fff5f5';
                          }
                          return '#f8fafc';
                        })(),
                        borderRadius: "14px",
                        padding: "16px 20px",
                        mb: 1.5,
                        border: (() => {
                          const oldPrice = parseFloat(car.description4 || "0");
                          const newPrice = parseFloat(car.description2);
                          const hasDiscount = oldPrice > newPrice && oldPrice > 0;
                          
                          // Check if it's a used car
                          const badgeLower = (car.badge || "").toLowerCase();
                          // Treat km0 cars the same as used cars - don't show discount
                          const isUsedOrKm0Car = 
                            badgeLower !== "nuovo";
                          
                          // Only use red border for new cars with discount
                          if (hasDiscount && !isUsedOrKm0Car) {
                            return '1px solid #fed7d7';
                          }
                          return '1px solid #e2e8f0';
                        })(),
                      }}
                    >
                      {/* Price below image removed to avoid duplication when ribbon is shown */}
                      {/* <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 800,
                          display: "inline-flex",
                          alignItems: "center",
                          margin: 0,
                          fontSize: "1.2rem",
                          color: (() => {
                            const oldPrice = parseFloat(car.description4 || "0");
                            const newPrice = parseFloat(car.description2);
                            const hasDiscount = oldPrice > newPrice && oldPrice > 0;
                            
                            // Check if it's a used car
                            const badgeLower = (car.badge || "").toLowerCase();
                            // Treat km0 cars the same as used cars - don't show discount
                            const isUsedOrKm0Car = 
                              badgeLower !== "nuovo";
                            
                            // Only use red text for new cars with discount
                            if (hasDiscount && !isUsedOrKm0Car) {
                              return '#dc2626';
                            }
                            return '#1a202c';
                          })(),
                        }}
                      >
                        <FaEuroSign style={{ marginRight: "8px", fontSize: "1rem", color: '#2196f3' }} />
                        {formatPrice(car.description2)}
                      </Typography> */}

                      {(() => {
                        const oldPrice = parseFloat(car.description4 || "0");
                        const newPrice = parseFloat(car.description2);
                        const hasDiscount = oldPrice > newPrice && oldPrice > 0;
                        if (!hasDiscount) return null;

                        const difference = oldPrice - newPrice;
                        
                        // Check if the car is used
                        const badgeLower = (car.badge || "").toLowerCase();
                        // Treat km0 cars the same as used cars - don't show discount
                        const isUsedOrKm0Car = 
                          badgeLower !== "nuovo";

                        return (
                          <>
                            {/* Only show original price with strikethrough for new cars */}
                            {!isUsedOrKm0Car && (
                              <Typography
                                variant="body2"
                                sx={{
                                  textDecoration: "line-through",
                                  color: "#9ca3af",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "2px",
                                  fontSize: "0.85rem",
                                  fontWeight: 500,
                                }}
                              >
                                <FaEuroSign style={{ fontSize: "0.7rem" }} />
                                {formatPrice(oldPrice, numberLocale)}
                              </Typography>
                            )}
                            
                            {/* Only show savings for new cars */}
                            {!isUsedOrKm0Car && (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color: "#059669",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  fontSize: "0.85rem",
                                }}
                              >
                                <FaCheckCircle style={{ fontSize: "0.8rem" }} />
                                {t('saveAmount', { amount: formatPrice(difference, numberLocale) })}
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </Box>

                    <Box sx={{ 
                      display: 'flex', 
                      gap: 2,
                      alignItems: 'center',
                      mb: 1,
                      flexWrap: 'wrap',
                    }}>
                      {(() => {
                        const badgeLower = (car.badge || "").toLowerCase();
                        // Keep existing logic for mileage display
                        const isMileageAllowed =
                          badgeLower !== "nuovo" &&
                          badgeLower !== "km zero" &&
                          badgeLower !== "km0" &&
                          badgeLower !== "km 0";

                        if (car.description1 && isMileageAllowed) {
                          return (
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 0.75,
                              backgroundColor: '#f8fafc',
                              padding: '6px 12px',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                            }}>
                              <FaTachometerAlt size={14} color="#64748b" />
                              <Typography sx={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>
                                {formatPrice(car.description1, numberLocale)} km
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      })()}
                      
                      {car.description3 && (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.75,
                          backgroundColor: '#f8fafc',
                          padding: '6px 12px',
                          borderRadius: '10px',
                          border: '1px solid #e2e8f0',
                        }}>
                          <FaCalendarAlt size={14} color="#64748b" />
                          <Typography sx={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>
                            {car.description3}
                          </Typography>
                        </Box>
                      )}

                      {/* Fuel information - check if available in car.fuel, car.description5, or other field */}
                      {(car.fuel || car.description5) && (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.75,
                          backgroundColor: '#f8fafc',
                          padding: '6px 12px',
                          borderRadius: '10px',
                          border: '1px solid #e2e8f0',
                        }}>
                          <FaGasPump size={14} color="#64748b" />
                          <Typography sx={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>
                            {car.fuel || car.description5}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </motion.div>
                  </Box>{/* end verbose-content wrapper */}
                </Box>{/* end outer content box */}

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: 1,
                    width: '100%',
                    mt: { xs: 0.5, md: 0 },
                    mb: { xs: 0.5, md: 1.5 },
                  }}
                >
                  {isCatalogItem(car) ? (
                    <>
                      {/* Catalog specs row */}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                        {car.fuel_type && (
                          <Chip
                            icon={<FaGasPump style={{ fontSize: '0.7rem' }} />}
                            label={car.fuel_type}
                            size="small"
                            sx={{ height: 22, fontSize: '0.72rem', bgcolor: '#f0fdf4', color: '#166534', fontWeight: 600, border: '1px solid #bbf7d0' }}
                          />
                        )}
                        {car.power_hp && (
                          <Chip
                            label={`${car.power_hp} CV`}
                            size="small"
                            sx={{ height: 22, fontSize: '0.72rem', bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700, border: '1px solid #bfdbfe' }}
                          />
                        )}
                        {car.transmission && (
                          <Chip
                            label={car.transmission}
                            size="small"
                            sx={{ height: 22, fontSize: '0.72rem', bgcolor: '#faf5ff', color: '#6b21a8', fontWeight: 600, border: '1px solid #e9d5ff' }}
                          />
                        )}
                        {car.num_optional_equipment > 0 && (
                          <Chip
                            label={`${car.num_optional_equipment} ${t('optionalShort')}`}
                            size="small"
                            sx={{ height: 22, fontSize: '0.72rem', bgcolor: '#fff7ed', color: '#c2410c', fontWeight: 600, border: '1px solid #fed7aa' }}
                          />
                        )}
                      </Box>

                      {/* Primary CTA: request quote */}
                      <MotionButton
                        size="small"
                        onClick={() => handleRichiediPreventivoConfig(car)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        startIcon={<FaComments style={{ fontSize: '0.9rem' }} />}
                        disabled={isLoading}
                        sx={{
                          fontSize: '0.85rem',
                          borderRadius: '12px',
                          backgroundColor: '#6366f1',
                          color: '#fff',
                          padding: '10px 18px',
                          textTransform: 'none',
                          fontWeight: 700,
                          minWidth: 'auto',
                          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                          height: '42px',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            backgroundColor: '#4f46e5',
                            boxShadow: '0 6px 16px rgba(99, 102, 241, 0.4)',
                            transform: 'translateY(-1px)',
                          },
                          '&.Mui-disabled': { backgroundColor: '#e2e8f0', color: '#94a3b8' },
                        }}
                      >
                        {t('requestQuote')}
                      </MotionButton>

                      {/* Secondary CTA: open trim dialog */}
                      <MotionButton
                        size="small"
                        onClick={() => handleOpenCatalogDialog(car)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        startIcon={<FaCog style={{ fontSize: '0.85rem' }} />}
                        disabled={isLoading}
                        sx={{
                          fontSize: '0.82rem',
                          borderRadius: '12px',
                          backgroundColor: '#f0f0ff',
                          color: '#4f46e5',
                          padding: '8px 16px',
                          textTransform: 'none',
                          fontWeight: 600,
                          minWidth: 'auto',
                          border: '1.5px solid #c7d2fe',
                          height: '38px',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            backgroundColor: '#e0e7ff',
                            borderColor: '#a5b4fc',
                            transform: 'translateY(-1px)',
                          },
                          '&.Mui-disabled': { backgroundColor: '#e2e8f0', color: '#94a3b8' },
                        }}
                      >
                        {t('seeOptionsAndColors')}
                      </MotionButton>
                    </>
                  ) : (
                    <>
                      <MotionButton
                        size="small"
                        onClick={() => handleBookVisit(car)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        startIcon={<FaCalendarAlt style={{ fontSize: '0.9rem' }} />}
                        disabled={isLoading}
                        sx={{
                          fontSize: '0.85rem',
                          borderRadius: '12px',
                          backgroundColor: '#10b981',
                          color: '#fff',
                          padding: '10px 18px',
                          textTransform: 'none',
                          fontWeight: 700,
                          minWidth: 'auto',
                          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                          height: '42px',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            backgroundColor: '#059669',
                            boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)',
                            transform: 'translateY(-1px)',
                          },
                          '&.Mui-disabled': { backgroundColor: '#e2e8f0', color: '#94a3b8' },
                        }}
                      >
                        {t('bookVisit')}
                      </MotionButton>
                      <MotionButton
                        size="small"
                        onClick={() => handleRichiediPreventivoStock(car)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        startIcon={<FaComments style={{ fontSize: '0.85rem' }} />}
                        disabled={isLoading}
                        sx={{
                          fontSize: '0.82rem',
                          borderRadius: '12px',
                          backgroundColor: '#f8fafc',
                          color: '#475569',
                          padding: '8px 16px',
                          textTransform: 'none',
                          fontWeight: 600,
                          minWidth: 'auto',
                          border: '1.5px solid #e2e8f0',
                          height: '38px',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            backgroundColor: '#f1f5f9',
                            transform: 'translateY(-1px)',
                          },
                          '&.Mui-disabled': { backgroundColor: '#e2e8f0', color: '#94a3b8' },
                        }}
                      >
                        {t('requestQuote')}
                      </MotionButton>
                    </>
                  )}
                </Box>

                {/* Promotion Document Button - Show when URL is available regardless of promotion/promotionDetails */}
                {!isCatalogItem(car) && car.promotion_document__url && (
                  <MotionButton
                    size="small"
                    component="a"
                    href={car.promotion_document__url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    startIcon={
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '16px',
                          height: '16px',
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                      </Box>
                    }
                    sx={{
                      fontSize: '0.85rem',
                      borderRadius: '12px',
                      backgroundColor: '#f5f3ff',
                      color: '#6d28d9',
                      padding: '10px 18px',
                      textTransform: 'none',
                      fontWeight: 600,
                      minWidth: 'auto',
                      border: '2px solid #ddd6fe',
                      height: '42px',
                      marginBottom: '10px',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        backgroundColor: '#ede9fe',
                        borderColor: '#c4b5fd',
                        transform: 'translateY(-1px)',
                      },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      trackChatEvent.viewPromotionDocument && trackChatEvent.viewPromotionDocument(car.vehicleid, car.promotion || t('promoFallbackLabel'));
                    }}
                  >
                    {t('specialOffer')}
                  </MotionButton>
                )}
                
                {/* Vehicle Page Button - only for stock, not catalog */}
                {!isCatalogItem(car) && car.url && (
                  <MotionButton
                    size="small"
                    component="a"
                    href={`${car.url}${car.url.includes('?') ? '&' : '?'}utm_source=carousel_mia_${car.vehicleid}&utm_medium=vehicle_card_mia&utm_campaign=vehicle_listing_mia`}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    startIcon={<FaExternalLinkAlt style={{ fontSize: '0.9rem' }} />}
                    sx={{
                      fontSize: '0.85rem',
                      borderRadius: '12px',
                      backgroundColor: '#f8fafc',
                      color: '#475569',
                      padding: '10px 18px',
                      textTransform: 'none',
                      fontWeight: 600,
                      minWidth: 'auto',
                      border: '2px solid #e2e8f0',
                      height: '42px',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        backgroundColor: '#f1f5f9',
                        borderColor: '#cbd5e1',
                        transform: 'translateY(-1px)',
                      },
                    }}
                    onClick={() => trackChatEvent.openVehiclePage(car.vehicleid, car.title)}
                  >
                    {t('technicalSheet')}
                  </MotionButton>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <VehicleDialog
        open={open}
        handleClose={handleClose}
        vehicleData={vehicleData}
        vehicleid={selectedVehicleId}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        showFullScreenGallery={showFullScreenGallery}
      />

      <CatalogTrimDialog
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        car={selectedCatalogCar}
        onAskAssistant={(msg) => handleSend(msg, null, null, { type: 'chat_message', sender: 'user' })}
      />
    </>
  );
};

export default SearchPanelCarousel; 
